/* Opening hours of a zone: weekly slots, each playing a daypart's mix. Outside them the zone is silent.
   No slots at all means open around the clock. Saving takes effect within ~20 s. */
import { For, Index, Show, createEffect, createSignal } from 'solid-js';
import { api, daypartName, Slot, Zone } from '../api';
import { ErrorLine, useAction } from '../ui';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const hhmm = (t: string) => t.slice(0, 5);
const STARTER: Slot[] = [1, 2, 3, 4, 5].map((d) => ({ weekday: d, start: '09:00', end: '18:00', daypart: 'default' }))
  .concat([{ weekday: 6, start: '10:00', end: '17:00', daypart: 'default' }]);

export default function ScheduleEditor(props: { zone: Zone; admin: boolean; onSaved: (z: Zone) => void }) {
  const [slots, setSlots] = createSignal<Slot[]>([]);
  const [always, setAlways] = createSignal(true);
  const a = useAction();
  const saved = () => props.zone.schedule.map((s) => ({ ...s, start: hhmm(s.start), end: hhmm(s.end) }));
  createEffect(() => { setSlots(saved()); setAlways(props.zone.schedule.length === 0); });

  const dayparts = () => ['default', ...Object.keys(props.zone.genre_mix ?? {}).filter((d) => d !== 'default')];
  const dirty = () => JSON.stringify(always() ? [] : slots()) !== JSON.stringify(saved());
  const onDay = (d: number) => slots().map((s, i) => ({ s, i })).filter((x) => x.s.weekday === d);
  const set = (i: number, patch: Partial<Slot>) => setSlots(slots().map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => setSlots(slots().filter((_, k) => k !== i));
  const add = (d: number) => {
    const mine = onDay(d);
    const last = mine[mine.length - 1]?.s;
    setSlots([...slots(), last ? { weekday: d, start: last.end, end: last.end < '22:00' ? '23:00' : '00:00', daypart: 'default' }
      : { weekday: d, start: '09:00', end: '18:00', daypart: 'default' }]);
  };
  const copyMonday = (to: number[]) => {
    const mon = slots().filter((s) => s.weekday === 1);
    setSlots([...slots().filter((s) => !to.includes(s.weekday)), ...to.flatMap((d) => mon.map((s) => ({ ...s, weekday: d })))]
      .sort((x, y) => x.weekday - y.weekday || x.start.localeCompare(y.start)));
  };
  const choose = (open24: boolean) => {
    setAlways(open24);
    if (!open24 && slots().length === 0) setSlots(STARTER.map((s) => ({ ...s })));
  };
  const save = () => a.run(async () => {
    props.onSaved(await api<Zone>(`/zones/${props.zone.id}/schedule`, { method: 'PUT', json: { slots: always() ? [] : slots() } }));
  });

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap gap-4 text-sm" role="radiogroup" aria-label="Opening hours">
        <label class="flex items-center gap-2"><input type="radio" class="radio radio-primary radio-sm" name={`hours-${props.zone.id}`}
          checked={always()} disabled={!props.admin} onChange={() => choose(true)} /> Open around the clock</label>
        <label class="flex items-center gap-2"><input type="radio" class="radio radio-primary radio-sm" name={`hours-${props.zone.id}`}
          checked={!always()} disabled={!props.admin} onChange={() => choose(false)} /> Set opening hours</label>
      </div>

      <Show when={!always()}>
        <p class="text-sm text-ink-3">Outside these hours the zone is silent; a song that is playing at closing time finishes.
          Hours that end at or before they start run past midnight (22:00 – 02:00). Times are in {props.zone.timezone}.</p>
        <table class="table table-sm">
          <tbody>
            <For each={DAYS}>
              {(day, di) => (
                <tr class="align-top">
                  <th scope="row" class="w-28 pt-3 font-semibold">{day}</th>
                  <td class="flex flex-col gap-2">
                    <Index each={onDay(di() + 1)} fallback={<span class="pt-1.5 text-sm text-ink-3">Closed</span>}>
                      {(x) => (
                        <div class="flex flex-wrap items-center gap-2">
                          <input type="time" class="input input-sm input-bordered mono w-36" value={x().s.start} disabled={!props.admin}
                            aria-label={`${day} opens`} onChange={(e) => set(x().i, { start: e.currentTarget.value })} />
                          <span class="text-ink-3">to</span>
                          <input type="time" class="input input-sm input-bordered mono w-36" value={x().s.end} disabled={!props.admin}
                            aria-label={`${day} closes`} onChange={(e) => set(x().i, { end: e.currentTarget.value })} />
                          <Show when={x().s.end <= x().s.start}><span class="text-xs text-ink-3">next day</span></Show>
                          <select class="select select-sm select-bordered" value={x().s.daypart} disabled={!props.admin}
                            aria-label={`${day} ${x().s.start} music`} onChange={(e) => set(x().i, { daypart: e.currentTarget.value })}>
                            <For each={dayparts()}>{(dp) => <option value={dp}>{dp === 'default' ? 'Default mix' : daypartName(dp)}</option>}</For>
                          </select>
                          <Show when={props.admin}>
                            <button class="btn btn-ghost btn-xs" aria-label={`Remove ${day} ${x().s.start}`} onClick={() => remove(x().i)}>✕</button>
                          </Show>
                        </div>
                      )}
                    </Index>
                  </td>
                  <td class="w-24 text-right">
                    <Show when={props.admin}><button class="btn btn-ghost btn-xs" onClick={() => add(di() + 1)}>+ Hours</button></Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
        <Show when={props.admin}>
          <div class="flex flex-wrap gap-2">
            <button class="btn btn-ghost btn-sm" onClick={() => copyMonday([2, 3, 4, 5])}>Copy Monday to Tue–Fri</button>
            <button class="btn btn-ghost btn-sm" onClick={() => copyMonday([2, 3, 4, 5, 6, 7])}>Copy Monday to every day</button>
          </div>
        </Show>
      </Show>

      <Show when={props.admin && dirty()}>
        <div class="flex gap-2">
          <button class="btn btn-sm btn-primary" disabled={a.busy()} onClick={save}>{a.busy() ? 'Saving…' : 'Save opening hours'}</button>
          <button class="btn btn-ghost btn-sm" onClick={() => { setSlots(saved()); setAlways(props.zone.schedule.length === 0); }}>Undo changes</button>
        </div>
      </Show>
      <ErrorLine error={a.error} />
    </div>
  );
}
